import React, { useState, useEffect } from 'react';
import { Upload, Search, Trash2, Edit2, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { uploadImage, validateImageFile, deleteImage } from '../../firebase/storage';
import {
  getMediaBankImages,
  addMediaBankImage,
  updateMediaBankImage,
  deleteMediaBankImage
} from '../../firebase/services';

const MediaBank = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    files: [],
    filesData: []
  });

  // Load images
  const loadImages = async () => {
    try {
      setLoading(true);
      const allImages = await getMediaBankImages(searchTerm, 1000); // Get all, filter client-side
      setImages(allImages);
    } catch (error) {
      console.error('Error loading images:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadImages();
  }, [searchTerm]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    const validFiles = [];
    const filesData = [];

    for (const file of selectedFiles) {
      try {
        validateImageFile(file);
        validFiles.push(file);
        filesData.push({
          file,
          name: file.name.replace(/\.[^/.]+$/, ''),
          description: '',
          preview: URL.createObjectURL(file)
        });
      } catch (error) {
        alert(`${file.name}: ${error.message}`);
      }
    }

    if (validFiles.length > 0) {
      setUploadForm({
        files: validFiles,
        filesData
      });
    }
  };

  // Update individual file data
  const updateFileData = (index, field, value) => {
    setUploadForm(prev => ({
      ...prev,
      filesData: prev.filesData.map((data, i) =>
        i === index ? { ...data, [field]: value } : data
      )
    }));
  };

  // Remove a file from the upload queue
  const removeFile = (index) => {
    setUploadForm(prev => ({
      files: prev.files.filter((_, i) => i !== index),
      filesData: prev.filesData.filter((_, i) => i !== index)
    }));
  };

  // Handle upload
  const handleUpload = async () => {
    if (uploadForm.files.length === 0) {
      alert('Please select at least one image');
      return;
    }

    // Validate all files have names
    const missingNames = uploadForm.filesData.filter(data => !data.name.trim());
    if (missingNames.length > 0) {
      alert('Please provide names for all images');
      return;
    }

    try {
      setUploading(true);

      let successCount = 0;
      let failCount = 0;

      // Upload all files
      for (const fileData of uploadForm.filesData) {
        try {
          // Upload to Firebase Storage
          const url = await uploadImage(fileData.file, 'media-bank');

          // Save to Firestore
          await addMediaBankImage({
            url,
            name: fileData.name,
            description: fileData.description || ''
          });

          successCount++;
        } catch (error) {
          console.error(`Error uploading ${fileData.name}:`, error);
          failCount++;
        }
      }

      // Reset form
      setUploadForm({ files: [], filesData: [] });
      setIsUploadDialogOpen(false);

      // Reload images
      await loadImages();

      // Show result
      if (failCount === 0) {
        alert(`Successfully uploaded ${successCount} image${successCount > 1 ? 's' : ''}!`);
      } else {
        alert(`Uploaded ${successCount} image(s). ${failCount} failed.`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload images: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  // Handle edit
  const handleEdit = (image) => {
    setEditingImage({
      ...image,
      editName: image.name,
      editDescription: image.description
    });
    setIsEditDialogOpen(true);
  };

  // Handle update
  const handleUpdate = async () => {
    if (!editingImage) return;

    try {
      await updateMediaBankImage(editingImage.firebaseId, {
        name: editingImage.editName,
        description: editingImage.editDescription
      });

      setIsEditDialogOpen(false);
      setEditingImage(null);
      await loadImages();

      alert('Image updated successfully!');
    } catch (error) {
      console.error('Update error:', error);
      alert('Failed to update image');
    }
  };

  // Handle delete
  const handleDelete = async (image) => {
    if (!window.confirm(`Delete "${image.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      // Delete from Storage
      await deleteImage(image.url);

      // Delete from Firestore
      await deleteMediaBankImage(image.firebaseId);

      await loadImages();

      alert('Image deleted successfully!');
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete image');
    }
  };

  // Pagination
  const totalPages = Math.ceil(images.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedImages = images.slice(startIndex, endIndex);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Media Bank</h2>
          <p className="text-gray-600 mt-1">
            Manage your image library ({images.length} images)
          </p>
        </div>
        <Button
          onClick={() => setIsUploadDialogOpen(true)}
          className="bg-[#4a1a1f] hover:bg-[#5a2a2f] text-white"
        >
          <Upload size={18} className="mr-2" />
          Upload Image
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <Input
            type="text"
            placeholder="Search by name or description..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="pl-10"
          />
        </div>
        <Select value={pageSize.toString()} onValueChange={(val) => {
          setPageSize(parseInt(val));
          setCurrentPage(1);
        }}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="20">20 per page</SelectItem>
            <SelectItem value="50">50 per page</SelectItem>
            <SelectItem value="100">100 per page</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Image Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4a1a1f] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading images...</p>
        </div>
      ) : paginatedImages.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <ImageIcon size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 mb-4">
            {searchTerm ? 'No images found matching your search.' : 'No images in media bank yet.'}
          </p>
          <Button onClick={() => setIsUploadDialogOpen(true)}>
            Upload Your First Image
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {paginatedImages.map((image) => (
              <div
                key={image.firebaseId}
                className="group relative bg-white border rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
              >
                {/* Image */}
                <div className="aspect-square bg-gray-100">
                  <img
                    src={image.url}
                    alt={image.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Info */}
                <div className="p-3">
                  <p className="font-medium text-sm truncate" title={image.name}>
                    {image.name}
                  </p>
                  {image.description && (
                    <p className="text-xs text-gray-500 truncate mt-1" title={image.description}>
                      {image.description}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    className="bg-white/90 hover:bg-white"
                    onClick={() => handleEdit(image)}
                  >
                    <Edit2 size={14} />
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="bg-white/90 hover:bg-white text-red-600"
                    onClick={() => handleDelete(image)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center pt-4 border-t">
              <p className="text-sm text-gray-600">
                Showing {startIndex + 1}-{Math.min(endIndex, images.length)} of {images.length}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                >
                  Previous
                </Button>
                <span className="px-4 py-2 text-sm">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Upload Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upload Images</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="file">Select Images *</Label>
              <Input
                id="file"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                onChange={handleFileSelect}
                disabled={uploading}
                multiple
              />
              <p className="text-xs text-gray-500 mt-1">
                JPG, PNG, WebP, GIF (max 5MB each). Select multiple files to upload at once.
              </p>
            </div>

            {uploadForm.filesData.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="font-semibold">
                    {uploadForm.filesData.length} image{uploadForm.filesData.length > 1 ? 's' : ''} selected
                  </p>
                </div>

                <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                  {uploadForm.filesData.map((fileData, index) => (
                    <div key={index} className="border rounded-lg p-4 space-y-3">
                      <div className="flex gap-4">
                        {/* Preview */}
                        <div className="w-24 h-24 flex-shrink-0">
                          <img
                            src={fileData.preview}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-full object-cover rounded bg-gray-50"
                          />
                        </div>

                        {/* Form Fields */}
                        <div className="flex-1 space-y-3">
                          <div>
                            <Label>Image Name *</Label>
                            <Input
                              value={fileData.name}
                              onChange={(e) => updateFileData(index, 'name', e.target.value)}
                              placeholder="e.g., Black Abaya Front View"
                              disabled={uploading}
                            />
                          </div>

                          <div>
                            <Label>Description (optional)</Label>
                            <Input
                              value={fileData.description}
                              onChange={(e) => updateFileData(index, 'description', e.target.value)}
                              placeholder="Add details..."
                              disabled={uploading}
                            />
                          </div>
                        </div>

                        {/* Remove Button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(index)}
                          disabled={uploading}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setIsUploadDialogOpen(false);
                  setUploadForm({ files: [], filesData: [] });
                }}
                disabled={uploading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={uploading || uploadForm.files.length === 0}
                className="bg-[#4a1a1f] hover:bg-[#5a2a2f]"
              >
                {uploading
                  ? 'Uploading...'
                  : `Upload ${uploadForm.files.length} Image${uploadForm.files.length > 1 ? 's' : ''}`
                }
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Image Details</DialogTitle>
          </DialogHeader>
          {editingImage && (
            <div className="space-y-4">
              <div className="border rounded-lg p-2">
                <img
                  src={editingImage.url}
                  alt={editingImage.name}
                  className="w-full h-48 object-contain bg-gray-50 rounded"
                />
              </div>

              <div>
                <Label htmlFor="editName">Image Name *</Label>
                <Input
                  id="editName"
                  value={editingImage.editName}
                  onChange={(e) => setEditingImage(prev => ({ ...prev, editName: e.target.value }))}
                />
              </div>

              <div>
                <Label htmlFor="editDescription">Description</Label>
                <Textarea
                  id="editDescription"
                  value={editingImage.editDescription}
                  onChange={(e) => setEditingImage(prev => ({ ...prev, editDescription: e.target.value }))}
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsEditDialogOpen(false);
                    setEditingImage(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdate}
                  className="bg-[#4a1a1f] hover:bg-[#5a2a2f]"
                >
                  Update Image
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MediaBank;
